/**
 * Visualization View Base Class
 * 
 * Base class for all visualization views.
 * Extends BaseViewHTML and provides common visualization functionality.
 */

import { BaseViewHTML } from '../../systems/base-view-html.js';
import type { VisualizationSignal, VisualizationMeta } from '../../types/visualization.js';
import { getRenderer } from './registry.js';
import { validateFull } from './validation.js';
import { designTokens, escapeHtml } from '../../core/design-system.js';
import { log } from '../../services/logging/index.js';

/**
 * Base class for visualization views
 */
export class VisualizationView extends BaseViewHTML {
  protected signal: VisualizationSignal;
  protected errorMessage: string | null = null;
  private currentLayout: { width: number; height: number } | null = null;
  private stockChartClickHandler: ((e: MouseEvent) => void) | null = null;

  constructor(
    processId: string,
    viewId: string,
    signal: VisualizationSignal
  ) {
    // Determine supported sizes for specific visualization types
    // 0 = Half height, normal width
    // 1 = Full height, normal width
    // 2 = Half height, double width
    // 3 = Full height, double width
    let sizes: number[] = [1]; // Default: full height, normal width
    
    // Stock chart: double-wide half-height and double-wide full-height
    if (signal.type === 'STOCK_CHART') {
      sizes = [ 2, 0, 3]; // [2] = double+half (default), [3] = double+full
    }
    
    super(processId, viewId, {
      showDismissButton: true,
      resizable: true,
      priority: signal.meta?.priority ? Math.round(signal.meta.priority * 100) : 50,
      sizes: sizes
    });

    this.signal = signal;
    
    // Set up geometry change callback to track size changes
    this.onGeometryChange = (geometry, metadata) => {
      // Only update if we have valid dimensions
      const width = geometry.width ?? 0;
      const height = geometry.height ?? 0;
      if (width > 0 && height > 0) {
        this.currentLayout = {
          width: width,
          height: height
        };
        // Trigger re-render when size changes
        this.markDirty();
      }
    };
    
    // Initialize layout from current geometry (will be updated when geometry is set)
    // Use fallback values that match the default size for stock chart (double width, half height)
    const initWidth = this.geometry.width ?? 0;
    const initHeight = this.geometry.height ?? 0;
    this.currentLayout = {
      width: initWidth > 0 ? initWidth : 800,
      height: initHeight > 0 ? initHeight : 400
    };
  }

  /**
   * Update visualization with new signal data
   */
  updateSignal(newSignal: VisualizationSignal): void {
    // Validate new signal
    const validation = validateFull(newSignal);
    if (!validation.valid) {
      this.errorMessage = validation.error || 'Invalid visualization signal';
      this.markDirty();
      log.error(`[VisualizationView] Invalid signal update: ${this.errorMessage}`);
      return;
    }

    this.signal = newSignal;
    this.errorMessage = null;
    this.markDirty();
  }

  /**
   * Override mount to set up event listeners for interactive elements
   */
  mount(container: HTMLElement): void {
    // Call parent mount first
    super.mount(container);
    
    // Set up period button handlers for stock charts
    if (this.signal.type === 'STOCK_CHART' && this.element) {
      this.setupStockChartInteractions();
    }
  }

  /**
   * Set up event listeners for stock chart period buttons
   */
  private setupStockChartInteractions(): void {
    if (!this.element) return;
    
    // Remove existing handler if present (prevent duplicates)
    if (this.stockChartClickHandler) {
      this.element.removeEventListener('click', this.stockChartClickHandler);
    }
    
    // Use event delegation for period buttons (handles re-renders)
    this.stockChartClickHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const button = target.closest('.stock-chart-period-btn') as HTMLElement;
      
      if (button && button.dataset.period) {
        const period = button.dataset.period as '1D' | '5D' | '1M' | '6M' | 'YTD' | '1Y' | '5Y' | 'MAX';
        
        // Update signal with new selected period
        const updatedSignal: VisualizationSignal = {
          ...this.signal,
          data: {
            ...(this.signal.data as any),
            selectedPeriod: period
          }
        };
        
        // Update and re-render
        this.updateSignal(updatedSignal);
        
        log.debug(`[VisualizationView] Period changed to ${period}`, {
          viewId: this.id,
          signalId: this.signal.id
        });
      }
    };
    
    this.element.addEventListener('click', this.stockChartClickHandler);
  }

  /**
   * Get content HTML for this visualization
   */
  protected getContentHTML(): string {
    // Show error if validation failed
    if (this.errorMessage) {
      return this.renderError(this.errorMessage);
    }

    // Validate signal
    const validation = validateFull(this.signal);
    if (!validation.valid) {
      return this.renderError(validation.error || 'Invalid visualization signal');
    }

    try {
      // Get renderer for this visualization type
      const renderer = getRenderer(this.signal.type);
      
      // Render visualization (data is typed as 'any' in VisualizationSignal)
      // Pass current layout dimensions to renderer
      // signal.layout can be a string (LayoutProfile) or object, so we use currentLayout
      // Use geometry if currentLayout is not set or has invalid dimensions
      const geomWidth = this.geometry.width ?? 0;
      const geomHeight = this.geometry.height ?? 0;
      const layout = (this.currentLayout && this.currentLayout.width > 0 && this.currentLayout.height > 0) 
        ? this.currentLayout 
        : {
            width: (geomWidth > 0 ? geomWidth : 800),
            height: (geomHeight > 0 ? geomHeight : 400)
          };
      
      // For stock chart, also pass metadata about current size state and viewId
      const layoutWithMetadata = this.signal.type === 'STOCK_CHART' 
        ? {
            ...layout,
            widthMode: this.metadata.widthMode,
            heightMode: this.metadata.heightMode
          }
        : layout;
      
      // Pass viewId in meta for event handling
      const metaWithViewId = this.signal.type === 'STOCK_CHART'
        ? {
            ...(this.signal.meta || {}),
            viewId: this.id
          }
        : this.signal.meta;
      
      const visualizationHTML = renderer(
        this.signal.data as any,
        metaWithViewId,
        layoutWithMetadata
      );

      // Build full HTML with header and visualization
      return this.buildVisualizationHTML(visualizationHTML);
    } catch (error) {
      log.error(`[VisualizationView] Rendering error`, {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      }, undefined, 'VisualizationView');
      return this.renderError(`Rendering error: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Build complete visualization HTML with header and content
   */
  private buildVisualizationHTML(visualizationHTML: string): string {
    const meta = this.signal.meta || {};
    const title = this.signal.title || meta.title || 'Visualization';
    const subtitle = this.signal.description || meta.subtitle;
    const sourceCortex = this.signal.sourceCortex || meta.sourceCortex;
    const ephemeral = this.signal.ephemeral === true;

    let headerHTML = `
      <div 
        role="banner"
        aria-label="Visualization header"
        style="
          padding: ${designTokens.padding.md};
          border-bottom: 1px solid ${designTokens.borderColor};
          margin-bottom: ${designTokens.padding.md};
        ">
        <div style="
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: ${designTokens.padding.md};
        ">
          <div style="flex: 1; min-width: 0;">
            <h3 
              id="viz-title-${this.signal.id}"
              style="
                margin: 0 0 ${subtitle ? designTokens.padding.xs : '0'} 0;
                font-size: ${designTokens.fontSize.lg};
                font-weight: 600;
                color: ${designTokens.contentColor};
                line-height: 1.3;
              ">${escapeHtml(title)}</h3>
            ${subtitle ? `
              <div 
                id="viz-subtitle-${this.signal.id}"
                style="
                  margin-top: ${designTokens.padding.xs};
                  font-size: ${designTokens.fontSize.sm};
                  color: ${designTokens.contentColorMuted};
                  line-height: 1.4;
                ">${escapeHtml(subtitle)}</div>
            ` : ''}
          </div>
          ${ephemeral ? `
            <div 
              role="status"
              aria-label="Ephemeral visualization"
              style="
                padding: ${designTokens.padding.xs} ${designTokens.padding.sm};
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid ${designTokens.borderColor};
                border-radius: ${designTokens.radius.sm};
                font-size: ${designTokens.fontSize.xs};
                color: ${designTokens.contentColorMuted};
                white-space: nowrap;
              ">Ephemeral</div>
          ` : ''}
        </div>
        ${sourceCortex ? `
          <div 
            aria-label="Source cortex"
            style="
              margin-top: ${designTokens.padding.xs};
              font-size: ${designTokens.fontSize.xs};
              color: ${designTokens.contentColorMuted};
            ">Source: ${escapeHtml(sourceCortex)}</div>
        ` : ''}
        ${meta.confidence !== undefined ? `
          <div 
            aria-label="Confidence level"
            style="
              margin-top: ${designTokens.padding.xs};
              font-size: ${designTokens.fontSize.xs};
              color: ${designTokens.contentColorMuted};
            ">Confidence: ${Math.round(meta.confidence * 100)}%</div>
        ` : ''}
      </div>
    `;

    return `
      <div 
        role="region"
        aria-labelledby="viz-title-${this.signal.id}"
        aria-label="Visualization: ${escapeHtml(title)}"
        style="
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        ">
        ${headerHTML}
        <div 
          role="main"
          aria-label="Visualization content"
          style="
            flex: 1;
            min-height: 0;
            overflow: auto;
            padding: ${designTokens.padding.md};
            animation: fadeIn 0.3s ease-in;
          ">
          ${visualizationHTML}
        </div>
      </div>
      <style>
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      </style>
    `;
  }

  /**
   * Render error state
   */
  private renderError(message: string): string {
    return `
      <div 
        role="alert"
        aria-live="assertive"
        aria-label="Visualization error"
        style="
          padding: ${designTokens.padding.xl};
          text-align: center;
          color: ${designTokens.contentColorMuted};
        ">
        <div 
          role="img"
          aria-label="Warning"
          style="
            font-size: ${designTokens.fontSize.lg};
            color: rgba(255, 100, 100, 0.8);
            margin-bottom: ${designTokens.padding.md};
          ">⚠️ Visualization Error</div>
        <div style="
          font-size: ${designTokens.fontSize.sm};
          color: ${designTokens.contentColorMuted};
        ">${escapeHtml(message)}</div>
      </div>
    `;
  }
}


